import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormGroup,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  Grid
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DocIcon,
  Settings as SettingsIcon,
  FileCopy as CopyIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import apiService from '../../services/api';
import ReferenceDocEditor from './ReferenceDocEditor';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Sources that are on unless a review type switches them off (review context update, 29 Sep 2026)
const DEFAULT_ON_SOURCES = [
  { key: 'includeFormulation', label: 'Gen 2 Clinical Formulation', summary: 'Formulation' },
  { key: 'includeReasoningTimeline', label: 'Gen 2 Reasoning Timeline', summary: 'Reasoning' },
  { key: 'includeSpecialistConsultations', label: 'Gen 2 Specialist Consultations', summary: 'Consultations' },
  { key: 'includeSafetyHistory', label: 'Alerts & Safety History', summary: 'Safety' },
  { key: 'includeLiveCalls', label: 'Live Calls (notes, risk, QA)', summary: 'Live calls' },
  { key: 'includeMemory', label: 'Memory (episode summaries, remembered facts)', summary: 'Memory' },
  { key: 'includeProgramme', label: 'Programme, Appointments & Consent', summary: 'Programme' }
];

function ReviewTypeManager({ token }) {
  const [reviewTypes, setReviewTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [docsDialogOpen, setDocsDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [availableModels, setAvailableModels] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    typeKey: '',
    name: '',
    description: '',
    icon: 'assessment',
    color: '#1976d2',
    contextConfig: {
      includeConversations: true,
      conversationLimit: 5,
      messageLimit: 100,
      includeMessageMetadata: false,
      includeSystemMessages: true,
      includeFunctionCalls: false,
      includeAssessments: true,
      includeGoals: true,
      includeCheckIns: true,
      includeJournalEntries: false,
      includeCareNotes: true,
      includeTriageData: true,
      includeOnboardingData: true,
      includeClientInfo: true,
      includeFormulation: true,
      includeReasoningTimeline: true,
      includeSpecialistConsultations: true,
      includeSafetyHistory: true,
      includeLiveCalls: true,
      includeMemory: true,
      includeProgramme: true
    },
    llmConfig: {
      provider: 'default',
      model: null,
      temperature: null,
      maxTokens: null
    },
    allowCustomInstructions: true,
    availableToRoles: ['admin', 'clinical_supervisor'],
    displayOrder: 100,
    isActive: true
  });

  useEffect(() => {
    loadReviewTypes();
    loadAvailableModels();
  }, [token]);

  const loadReviewTypes = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get('/review-types/all');
      setReviewTypes(response.data.data || []);
    } catch (err) {
      console.error('Error loading review types:', err);
      setError(err.response?.data?.message || 'Failed to load review types');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableModels = async () => {
    try {
      const data = await apiService.getAvailableLLMModels(token);
      setAvailableModels(data.models || data || []);
    } catch (err) {
      console.error('Error loading available models:', err);
    }
  };

  const getModelsForProvider = (provider) => {
    return availableModels.filter(m => m.provider === provider && m.isActive && !m.isDeprecated);
  };

  const handleCreate = () => {
    setSelectedType(null);
    setFormData({
      typeKey: '',
      name: '',
      description: '',
      icon: 'assessment',
      color: '#1976d2',
      contextConfig: {
        includeConversations: true,
        conversationLimit: 5,
        messageLimit: 100,
        includeMessageMetadata: false,
        includeSystemMessages: true,
        includeFunctionCalls: false,
        includeAssessments: true,
        includeGoals: true,
        includeCheckIns: true,
        includeJournalEntries: false,
        includeCareNotes: true,
        includeTriageData: true,
        includeOnboardingData: true,
        includeClientInfo: true,
        includeFormulation: true,
        includeReasoningTimeline: true,
        includeSpecialistConsultations: true,
        includeSafetyHistory: true,
        includeLiveCalls: true,
        includeMemory: true,
        includeProgramme: true
      },
      llmConfig: {
        provider: 'default',
        model: null,
        temperature: null,
        maxTokens: null
      },
      allowCustomInstructions: true,
      availableToRoles: ['admin', 'clinical_supervisor'],
      displayOrder: 100,
      isActive: true
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (reviewType) => {
    setSelectedType(reviewType);
    setFormData({
      typeKey: reviewType.typeKey,
      name: reviewType.name,
      description: reviewType.description || '',
      icon: reviewType.icon || 'assessment',
      color: reviewType.color || '#1976d2',
      contextConfig: reviewType.contextConfig || {},
      llmConfig: reviewType.llmConfig || { provider: 'default', model: null, temperature: null, maxTokens: null },
      allowCustomInstructions: reviewType.allowCustomInstructions !== false,
      availableToRoles: reviewType.availableToRoles || ['admin', 'clinical_supervisor'],
      displayOrder: reviewType.displayOrder || 100,
      isActive: reviewType.isActive !== false
    });
    setEditDialogOpen(true);
  };

  const handleView = (reviewType) => {
    setSelectedType(reviewType);
    setViewDialogOpen(true);
  };

  const handleManageDocs = (reviewType) => {
    setSelectedType(reviewType);
    setDocsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setError('');

      if (selectedType) {
        // Update existing
        await apiService.put(`/review-types/${selectedType._id}`, {
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          color: formData.color,
          contextConfig: formData.contextConfig,
          llmConfig: formData.llmConfig,
          allowCustomInstructions: formData.allowCustomInstructions,
          availableToRoles: formData.availableToRoles,
          displayOrder: parseInt(formData.displayOrder),
          isActive: formData.isActive
        });
        setSuccess('Review type updated successfully');
      } else {
        // Create new
        await apiService.post('/review-types', {
          typeKey: formData.typeKey,
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          color: formData.color,
          contextConfig: formData.contextConfig,
          llmConfig: formData.llmConfig,
          allowCustomInstructions: formData.allowCustomInstructions,
          availableToRoles: formData.availableToRoles,
          displayOrder: parseInt(formData.displayOrder)
        });
        setSuccess('Review type created successfully');
      }

      setEditDialogOpen(false);
      loadReviewTypes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save review type');
    }
  };

  const handleToggleActive = async (reviewType) => {
    try {
      await apiService.put(`/review-types/${reviewType._id}`, {
        isActive: !reviewType.isActive
      });
      setSuccess(`Review type ${reviewType.isActive ? 'deactivated' : 'activated'} successfully`);
      loadReviewTypes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update review type');
    }
  };

  const handleDelete = async (reviewType) => {
    if (reviewType.isSystemType) {
      setError('Cannot delete system review types. Deactivate instead.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${reviewType.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await apiService.delete(`/review-types/${reviewType._id}`);
      setSuccess('Review type deleted successfully');
      loadReviewTypes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete review type');
    }
  };

  const handleContextConfigChange = (field, value) => {
    setFormData({
      ...formData,
      contextConfig: {
        ...formData.contextConfig,
        [field]: value
      }
    });
  };

  const handleRoleToggle = (role) => {
    const currentRoles = formData.availableToRoles || [];
    if (currentRoles.includes(role)) {
      setFormData({
        ...formData,
        availableToRoles: currentRoles.filter(r => r !== role)
      });
    } else {
      setFormData({
        ...formData,
        availableToRoles: [...currentRoles, role]
      });
    }
  };

  const getContextSummary = (contextConfig) => {
    if (!contextConfig) return 'Default configuration';
    const enabled = [];
    if (contextConfig.includeConversations) enabled.push('Conversations');
    if (contextConfig.includeAssessments) enabled.push('Assessments');
    if (contextConfig.includeGoals) enabled.push('Goals');
    if (contextConfig.includeCheckIns) enabled.push('Check-ins');
    if (contextConfig.includeJournalEntries) enabled.push('Journals');
    if (contextConfig.includeCareNotes) enabled.push('Care Notes');
    if (contextConfig.includeTriageData) enabled.push('Triage');
    DEFAULT_ON_SOURCES.forEach(({ key, summary }) => {
      if (contextConfig[key] !== false) enabled.push(summary);
    });
    return enabled.length > 0 ? enabled.join(', ') : 'Minimal context';
  };

  const systemTypes = reviewTypes.filter(t => t.isSystemType);
  const customTypes = reviewTypes.filter(t => !t.isSystemType);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Review Type Manager</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create New Type
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label={`All Types (${reviewTypes.length})`} />
          <Tab label={`System Types (${systemTypes.length})`} />
          <Tab label={`Custom Types (${customTypes.length})`} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <ReviewTypeTable
            reviewTypes={reviewTypes}
            onView={handleView}
            onEdit={handleEdit}
            onManageDocs={handleManageDocs}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
            getContextSummary={getContextSummary}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Alert severity="info" sx={{ mb: 2 }}>
            System types are pre-configured and cannot be deleted. You can modify their settings and reference documents.
          </Alert>
          <ReviewTypeTable
            reviewTypes={systemTypes}
            onView={handleView}
            onEdit={handleEdit}
            onManageDocs={handleManageDocs}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
            getContextSummary={getContextSummary}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <ReviewTypeTable
            reviewTypes={customTypes}
            onView={handleView}
            onEdit={handleEdit}
            onManageDocs={handleManageDocs}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
            getContextSummary={getContextSummary}
          />
        </TabPanel>
      </Paper>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedType ? `Edit Review Type: ${selectedType.name}` : 'Create New Review Type'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Basic Info */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Basic Information
            </Typography>

            <TextField
              fullWidth
              label="Type Key"
              value={formData.typeKey}
              onChange={(e) => setFormData({ ...formData, typeKey: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
              disabled={!!selectedType}
              helperText="Unique identifier (e.g., safety_review, custom_analysis)"
              required
            />

            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TextField
                  fullWidth
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  fullWidth
                  label="Display Order"
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({ ...formData, displayOrder: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Icon</InputLabel>
                  <Select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    label="Icon"
                  >
                    <MenuItem value="assessment">Assessment</MenuItem>
                    <MenuItem value="warning">Warning</MenuItem>
                    <MenuItem value="psychology">Psychology</MenuItem>
                    <MenuItem value="trending_up">Trending Up</MenuItem>
                    <MenuItem value="search">Search</MenuItem>
                    <MenuItem value="medical_services">Medical</MenuItem>
                    <MenuItem value="edit_note">Edit Note</MenuItem>
                    <MenuItem value="chat">Chat</MenuItem>
                    <MenuItem value="bug_report">Bug Report</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />

            {/* Context Configuration */}
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SettingsIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                  Context Configuration
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure what client data is included when running reviews of this type.
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Data Sources</Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeConversations !== false}
                            onChange={(e) => handleContextConfigChange('includeConversations', e.target.checked)}
                          />
                        }
                        label="Conversations"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeAssessments || false}
                            onChange={(e) => handleContextConfigChange('includeAssessments', e.target.checked)}
                          />
                        }
                        label="Assessments"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeGoals || false}
                            onChange={(e) => handleContextConfigChange('includeGoals', e.target.checked)}
                          />
                        }
                        label="Goals"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeCheckIns || false}
                            onChange={(e) => handleContextConfigChange('includeCheckIns', e.target.checked)}
                          />
                        }
                        label="Check-ins"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeJournalEntries || false}
                            onChange={(e) => handleContextConfigChange('includeJournalEntries', e.target.checked)}
                          />
                        }
                        label="Journal Entries"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeCareNotes || false}
                            onChange={(e) => handleContextConfigChange('includeCareNotes', e.target.checked)}
                          />
                        }
                        label="Care Notes"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeTriageData || false}
                            onChange={(e) => handleContextConfigChange('includeTriageData', e.target.checked)}
                          />
                        }
                        label="Triage Data"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeOnboardingData || false}
                            onChange={(e) => handleContextConfigChange('includeOnboardingData', e.target.checked)}
                          />
                        }
                        label="Onboarding Data"
                      />
                      {DEFAULT_ON_SOURCES.map(({ key, label }) => (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              checked={formData.contextConfig[key] !== false}
                              onChange={(e) => handleContextConfigChange(key, e.target.checked)}
                            />
                          }
                          label={label}
                        />
                      ))}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeClientInfo !== false}
                            onChange={(e) => handleContextConfigChange('includeClientInfo', e.target.checked)}
                          />
                        }
                        label="Client Info"
                      />
                    </FormGroup>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>Conversation Options</Typography>
                    <TextField
                      fullWidth
                      label="Conversation Limit"
                      type="number"
                      value={formData.contextConfig.conversationLimit || 5}
                      onChange={(e) => handleContextConfigChange('conversationLimit', parseInt(e.target.value))}
                      sx={{ mb: 2 }}
                      inputProps={{ min: 1, max: 20 }}
                    />
                    <TextField
                      fullWidth
                      label="Message Limit (per conversation)"
                      type="number"
                      value={formData.contextConfig.messageLimit || 100}
                      onChange={(e) => handleContextConfigChange('messageLimit', parseInt(e.target.value))}
                      sx={{ mb: 2 }}
                      inputProps={{ min: 10, max: 500 }}
                    />

                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>Advanced Options</Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeMessageMetadata || false}
                            onChange={(e) => handleContextConfigChange('includeMessageMetadata', e.target.checked)}
                          />
                        }
                        label="Include Message Metadata"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeFunctionCalls || false}
                            onChange={(e) => handleContextConfigChange('includeFunctionCalls', e.target.checked)}
                          />
                        }
                        label="Include Function Calls"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.contextConfig.includeSystemMessages !== false}
                            onChange={(e) => handleContextConfigChange('includeSystemMessages', e.target.checked)}
                          />
                        }
                        label="Include System Messages"
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Divider sx={{ my: 1 }} />

            {/* Access Control */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Access Control
            </Typography>

            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.availableToRoles.includes('admin')}
                    onChange={() => handleRoleToggle('admin')}
                  />
                }
                label="Admin"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.availableToRoles.includes('clinical_supervisor')}
                    onChange={() => handleRoleToggle('clinical_supervisor')}
                  />
                }
                label="Clinical Supervisor"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.availableToRoles.includes('therapist')}
                    onChange={() => handleRoleToggle('therapist')}
                  />
                }
                label="Therapist"
              />
            </FormGroup>

            {/* LLM Configuration Override */}
            <Accordion sx={{ mt: 2, mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  LLM Configuration Override
                  {formData.llmConfig?.provider && formData.llmConfig.provider !== 'default' && (
                    <Chip label="Custom" size="small" color="info" sx={{ ml: 1 }} />
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Override the platform default LLM settings for this review type. Set to "Platform Default" to use the global settings.
                </Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={formData.llmConfig?.provider || 'default'}
                    label="Provider"
                    onChange={(e) => setFormData({
                      ...formData,
                      llmConfig: { ...formData.llmConfig, provider: e.target.value, model: null }
                    })}
                  >
                    <MenuItem value="default">Platform Default</MenuItem>
                    <MenuItem value="vertex_ai">Vertex AI</MenuItem>
                    <MenuItem value="gemini">Gemini</MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                  </Select>
                </FormControl>
                {formData.llmConfig?.provider && formData.llmConfig.provider !== 'default' && (
                  <>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Model</InputLabel>
                      <Select
                        value={formData.llmConfig?.model || ''}
                        label="Model"
                        onChange={(e) => setFormData({
                          ...formData,
                          llmConfig: { ...formData.llmConfig, model: e.target.value || null }
                        })}
                      >
                        <MenuItem value="">Provider Default</MenuItem>
                        {getModelsForProvider(formData.llmConfig?.provider).map(m => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.displayName} ({m.contextWindow?.toLocaleString()} tokens)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      label="Temperature"
                      type="number"
                      value={formData.llmConfig?.temperature ?? ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        llmConfig: { ...formData.llmConfig, temperature: e.target.value === '' ? null : parseFloat(e.target.value) }
                      })}
                      inputProps={{ min: 0, max: 2, step: 0.1 }}
                      helperText="0.0-2.0. Leave empty for platform default"
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="Max Output Tokens"
                      type="number"
                      value={formData.llmConfig?.maxTokens ?? ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        llmConfig: { ...formData.llmConfig, maxTokens: e.target.value === '' ? null : parseInt(e.target.value) }
                      })}
                      inputProps={{ min: 100, max: 32000, step: 256 }}
                      helperText="100-32000. Leave empty for platform default"
                    />
                  </>
                )}
              </AccordionDetails>
            </Accordion>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.allowCustomInstructions}
                  onChange={(e) => setFormData({ ...formData, allowCustomInstructions: e.target.checked })}
                />
              }
              label="Allow Custom Instructions"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {selectedType ? 'Save Changes' : 'Create Review Type'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>View Review Type</DialogTitle>
        <DialogContent>
          {selectedType && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={8}>
                  <Typography variant="subtitle2" gutterBottom>Name:</Typography>
                  <Typography variant="h6" sx={{ mb: 2 }}>{selectedType.name}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" gutterBottom>Type Key:</Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                    {selectedType.typeKey}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" gutterBottom>Description:</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>{selectedType.description || 'No description'}</Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" gutterBottom>Context Configuration:</Typography>
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="body2">
                  {getContextSummary(selectedType.contextConfig)}
                </Typography>
                {selectedType.contextConfig?.conversationLimit && (
                  <Typography variant="caption" display="block">
                    Conversations: {selectedType.contextConfig.conversationLimit}, Messages: {selectedType.contextConfig.messageLimit || 100}
                  </Typography>
                )}
              </Paper>

              <Typography variant="subtitle2" gutterBottom>Reference Documents:</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {selectedType.referenceDocuments?.length || 0} document(s) attached
              </Typography>

              <Typography variant="subtitle2" gutterBottom>Access:</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                {selectedType.availableToRoles?.map(role => (
                  <Chip key={role} label={role.replace('_', ' ')} size="small" />
                ))}
                <Chip
                  label={selectedType.isActive ? 'Active' : 'Inactive'}
                  color={selectedType.isActive ? 'success' : 'default'}
                  size="small"
                />
                {selectedType.isSystemType && (
                  <Chip label="System Type" icon={<LockIcon />} size="small" color="info" />
                )}
              </Box>

              <Typography variant="caption" color="text.secondary">
                Created: {new Date(selectedType.createdAt).toLocaleString()}<br />
                Updated: {new Date(selectedType.updatedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              handleManageDocs(selectedType);
            }}
            startIcon={<DocIcon />}
          >
            Manage Documents
          </Button>
          <Button
            onClick={() => {
              setViewDialogOpen(false);
              handleEdit(selectedType);
            }}
            variant="contained"
          >
            Edit Type
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reference Documents Dialog */}
      <Dialog open={docsDialogOpen} onClose={() => setDocsDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Reference Documents: {selectedType?.name}
        </DialogTitle>
        <DialogContent>
          {selectedType && (
            <ReferenceDocEditor
              reviewTypeId={selectedType._id}
              documents={selectedType.referenceDocuments || []}
              onUpdate={() => {
                loadReviewTypes();
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// Separate table component for reuse
function ReviewTypeTable({ reviewTypes, onView, onEdit, onManageDocs, onToggleActive, onDelete, getContextSummary }) {
  if (reviewTypes.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No review types found</Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Key</TableCell>
            <TableCell>Context</TableCell>
            <TableCell>Documents</TableCell>
            <TableCell>Access</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {reviewTypes.map((reviewType) => (
            <TableRow key={reviewType._id}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: reviewType.color || '#1976d2'
                    }}
                  />
                  {reviewType.name}
                  {reviewType.isSystemType && (
                    <Tooltip title="System Type">
                      <LockIcon fontSize="small" color="action" />
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {reviewType.typeKey}
                </Typography>
              </TableCell>
              <TableCell>
                <Tooltip title={getContextSummary(reviewType.contextConfig)}>
                  <Typography variant="caption" sx={{ maxWidth: 150, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getContextSummary(reviewType.contextConfig)}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Chip
                  label={reviewType.referenceDocuments?.length || 0}
                  size="small"
                  icon={<DocIcon />}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {reviewType.availableToRoles?.slice(0, 2).map(role => (
                    <Chip key={role} label={role.replace('_', ' ').substring(0, 10)} size="small" variant="outlined" />
                  ))}
                  {reviewType.availableToRoles?.length > 2 && (
                    <Chip label={`+${reviewType.availableToRoles.length - 2}`} size="small" variant="outlined" />
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={reviewType.isActive ? 'Active' : 'Inactive'}
                  color={reviewType.isActive ? 'success' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <IconButton size="small" onClick={() => onView(reviewType)} title="View">
                  <ViewIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onEdit(reviewType)} title="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onManageDocs(reviewType)} title="Manage Documents">
                  <DocIcon fontSize="small" />
                </IconButton>
                {!reviewType.isSystemType && (
                  <IconButton size="small" onClick={() => onDelete(reviewType)} title="Delete">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ReviewTypeManager;
